import React, { useEffect, useMemo, useState } from 'react'

import Box from 'components_new/atoms/Box'
import Button from 'components_new/atoms/Button'
import FormControl from 'components_new/atoms/FormControl'
import Icon from 'components_new/atoms/Icon'
import InputLabel from 'components_new/atoms/InputLabel'
import MenuItem from 'components_new/atoms/Menu/MenuItem'
import Pagination from 'components_new/atoms/Pagination'
import Select from 'components_new/atoms/Select'
import Table from 'components_new/atoms/Table'
import TableBody from 'components_new/atoms/Table/TableBody'
import TableCell from 'components_new/atoms/Table/TableCell'
import TableContainer from 'components_new/atoms/Table/TableContainer'
import TableHead from 'components_new/atoms/Table/TableHead'
import TableRow from 'components_new/atoms/Table/TableRow'
import Tag from 'components_new/atoms/Tag'
import Text from 'components_new/atoms/Text'

import EmptyPlaceholder from 'components_new/molecules/EmptyPlaceholder'
import LoadingSection from 'components_new/molecules/LoadingSection'
import SectionHeader from 'components_new/molecules/SectionHeader'
import SelectSearch from 'components_new/molecules/SelectSearch'
import SimpleDialog from 'components_new/molecules/SimpleDialog'
import SimpleTable from 'components_new/molecules/SimpleTable'

import DataHealthKpiCard from 'components_new/organisms/internal/DataHealth/KpiCard'
import DataHealthKpiGrid from 'components_new/organisms/internal/DataHealth/KpiGrid'
import DataHealthSectionCard from 'components_new/organisms/internal/DataHealth/SectionCard'

import { useSimFlawActions } from 'redux/actions/internal/SimFlaws'
import { useInternalSimFlaws } from 'redux/hooks/internal/SimFlaws'
import { SimFlawGroup } from 'redux/selectors/internal/SimFlaws'

import {
  asFlawType,
  FlawType,
  flawTypeColor,
  SettingRow
} from 'types/GlobalSimFlaw'
import { useTranslation } from 'language/translator'
import {
  attributeLabel,
  tableLabel
} from 'components_new/templates/internal/DataHealth/lib'

/** Table cards are tall, so a page of them is short. Matches the API. */
const GROUP_PAGE_SIZE = 3

/** Rows per page once a single table is filtered and its card pages rows. */
const ROW_PAGE_SIZE = 10

const COLUMNS = [
  { title: 'Kund', width: '18%' },
  { title: 'Attribut', width: '20%' },
  { title: 'ID', width: '12%' },
  { title: 'Värde' },
  { title: 'Typ', width: '14%' }
]

export interface DataHealthValidationTabProps {
  /** Total customers, for the "utan brister" sub-label. */
  customerCount: number
  /** null = all customers. Owned by the Datahälsa route. */
  selectedCustomerId: string | null
  selectedCustomerName: string | null
}

/**
 * The Datavalidering tab: which customers and tables have bad output data,
 * and what exactly is wrong with it.
 *
 * Two things changed when this became a tab. It no longer requires a customer
 * before it shows anything - it opens on every customer and filters down - and
 * the flat list became one card per affected table, because `sim_flaws` has no
 * severity and no history, so grouping and counting is the only way the data
 * can express that one table is worse than another.
 */
const DataHealthValidationTab = (props: DataHealthValidationTabProps) => {
  const { customerCount, selectedCustomerId, selectedCustomerName } = props

  const { t, translateEnum } = useTranslation()

  const simFlaws = useInternalSimFlaws()
  const { tryGetSimFlawGroups } = useSimFlawActions()

  const [showHelp, setShowHelp] = useState(false)
  const [groupPage, setGroupPage] = useState(1)
  const [rowPage, setRowPage] = useState(1)
  const [selectedTables, setSelectedTables] = useState<
    { id: string; title: string }[]
  >([])
  const [selectedType, setSelectedType] = useState<FlawType | null>(null)

  const settings = simFlaws.settings

  const tableOptions = useMemo(
    () =>
      Object.values(settings).map((table) => ({
        id: table.value,
        title: table.label
      })),
    [settings]
  )

  const selectTables = (tables: { id: string; title: string }[]) => {
    setSelectedTables(tables)
    setGroupPage(1)
    setRowPage(1)
  }

  const tableFilter = selectedTables.map((table) => table.id).join(',')

  /**
   * One table filtered means one card, and that card pages its own rows
   * instead of previewing a handful - this is where "Visa alla i Avtal" ends
   * up.
   */
  const isSingleTable = selectedTables.length === 1

  useEffect(() => {
    tryGetSimFlawGroups({
      customer_id: selectedCustomerId ?? undefined,
      limit: String(GROUP_PAGE_SIZE),
      offset: String((groupPage - 1) * GROUP_PAGE_SIZE),
      rows_limit: isSingleTable ? String(ROW_PAGE_SIZE) : undefined,
      rows_offset: isSingleTable
        ? String((rowPage - 1) * ROW_PAGE_SIZE)
        : undefined,
      table_name: tableFilter || undefined,
      type: selectedType ?? undefined
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCustomerId, tableFilter, selectedType, groupPage, rowPage])

  const aggregates = simFlaws.aggregates
  const topType = aggregates?.type_counts[0] ?? null

  const topTypeShare =
    topType && aggregates && aggregates.total_count > 0
      ? Math.round((topType.count / aggregates.total_count) * 100)
      : null

  const pageCount = isSingleTable
    ? Math.max(1, Math.ceil((simFlaws.rowCount ?? 0) / ROW_PAGE_SIZE))
    : Math.max(1, Math.ceil(simFlaws.groupCount / GROUP_PAGE_SIZE))

  const page = isSingleTable ? rowPage : groupPage
  const setPage = isSingleTable ? setRowPage : setGroupPage

  const renderGroup = (group: SimFlawGroup) => {
    const label = tableLabel(settings, group.table_name)

    return (
      <DataHealthSectionCard
        footer={
          <>
            <Text sx={{ color: 'text.disabled' }} variant="caption">
              {`Visar ${group.rows.length} av ${group.total_count} brister i ${label}`}
            </Text>
            {isSingleTable ? null : (
              <Button
                endIcon={<Icon fontSize="small" name="ArrowForwardOutlined" />}
                onClick={() =>
                  selectTables([{ id: group.table_name, title: label }])
                }
                size="small"
                sx={{ ml: 'auto' }}
                variant="text"
              >
                {`Visa alla i ${label}`}
              </Button>
            )}
          </>
        }
        icon="TableRowsOutlined"
        key={group.table_name}
        meta={[
          `${group.customer_count} ${
            group.customer_count === 1 ? 'kund' : 'kunder'
          }`,
          group.top_attribute
            ? `mest ${attributeLabel(
              settings,
              group.table_name,
              group.top_attribute
            )}`
            : null
        ]
          .filter(Boolean)
          .join(' · ')}
        pill={{ label: group.total_count.toLocaleString('sv-SE') }}
        title={label}
      >
        <SimpleTable
          columns={COLUMNS}
          disableSorting
          rows={group.rows.map((row) => ({
            // Deliberately no onClick: the five columns are the whole story
            // of a brist, so there is nothing for a row to open.
            id: row.id,
            cells: [
              <Text key="customer" sx={{ fontWeight: 600 }} variant="body2">
                {row.customer_name}
              </Text>,
              <Text
                key="attribute"
                sx={{ color: 'text.secondary' }}
                variant="body2"
              >
                {attributeLabel(settings, row.table_name, row.attribute_name)}
              </Text>,
              <Text
                key="id"
                sx={{
                  color: 'text.secondary',
                  fontVariantNumeric: 'tabular-nums'
                }}
                variant="body2"
              >
                {row.row_id}
              </Text>,
              <Text
                key="value"
                sx={{
                  fontFamily: 'ui-monospace, Menlo, monospace',
                  fontSize: 13,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {row.row_value ?? '—'}
              </Text>,
              <Tag
                color={flawTypeColor[row.type]}
                key="type"
                label={translateEnum('flaw_types', asFlawType(row.type)).title}
              />
            ]
          }))}
        />
      </DataHealthSectionCard>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1.5
        }}
      >
        <SelectSearch
          label={'Tabell'}
          multiple
          onChange={selectTables}
          options={tableOptions}
          selected={selectedTables}
          sort
          sx={{ minWidth: 220, mt: 0 }}
        />
        <FormControl color="primary" size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="type-select" shrink>
            Typ
          </InputLabel>
          <Select
            displayEmpty
            label={'Typ'}
            labelId="type-select"
            onChange={(event) => {
              setSelectedType(event.target.value as FlawType | null)
              setGroupPage(1)
              setRowPage(1)
            }}
            value={selectedType}
          >
            <MenuItem key={'null'} value={null as unknown as string}>
              Alla
            </MenuItem>
            {Object.values(FlawType).map((type) => (
              <MenuItem key={type} value={type}>
                {translateEnum('flaw_types', type).title}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          onClick={() => setShowHelp(true)}
          size="small"
          startIcon={<Icon fontSize="small" name="HelpOutline" />}
          sx={{ ml: 'auto' }}
          variant="text"
        >
          Vad validerar vi?
        </Button>
      </Box>

      {aggregates ? (
        <DataHealthKpiGrid>
          <DataHealthKpiCard
            label="Brister totalt"
            sub={selectedCustomerName ?? `alla ${customerCount} kunder`}
            value={aggregates.total_count.toLocaleString('sv-SE')}
          />
          <DataHealthKpiCard
            label="Kunder med brister"
            sub={
              selectedCustomerName
                ? 'vald kund'
                : `${Math.max(
                  customerCount - aggregates.customers_with_flaws,
                  0
                )} kunder utan brister`
            }
            value={aggregates.customers_with_flaws}
          />
          <DataHealthKpiCard
            label="Berörda tabeller"
            sub={`av ${aggregates.validated_tables} validerade`}
            value={aggregates.affected_tables}
          />
          <DataHealthKpiCard
            label="Vanligaste typ"
            sub={
              topType
                ? `${translateEnum(
                  'flaw_types',
                  asFlawType(topType.type)
                ).title.toLowerCase()} · ${topType.count.toLocaleString(
                  'sv-SE'
                )} st`
                : '–'
            }
            value={topTypeShare === null ? '–' : `${topTypeShare} %`}
          />
        </DataHealthKpiGrid>
      ) : null}

      {/*
        Loading on every fetch, not just the first: nothing polls the groups,
        so the only reason for a fetch is a changed filter - and keeping the
        previous filter's rows on screen while it runs shows brister the new
        filter excludes.
      */}
      {simFlaws.groupsFetching ? (
        <LoadingSection description={['Hämtar brister']} sx={{ pt: 4 }} />
      ) : simFlaws.groups.length === 0 ? (
        <EmptyPlaceholder background title="Det finns inga brister." />
      ) : (
        <>
          {simFlaws.groups.map(renderGroup)}
          {pageCount > 1 ? (
            <Pagination
              count={pageCount}
              onChange={(_, newPage) => setPage(newPage)}
              page={page}
              shape="rounded"
              sx={{ display: 'flex', justifyContent: 'center' }}
              variant="outlined"
            />
          ) : null}
        </>
      )}

      <SimpleDialog
        actions={
          <Button onClick={() => setShowHelp(false)} variant="text">
            {t('global', 'close')}
          </Button>
        }
        maxWidth="md"
        onClose={() => setShowHelp(false)}
        open={showHelp}
        title={'Vad validerar vi för data?'}
      >
        <TableContainer
          sx={{
            borderColor: 'divider',
            borderRadius: 1,
            borderStyle: 'solid',
            borderWidth: 1
          }}
        >
          <Table size="small" stickyHeader={true}>
            <TableHead sx={{ bgcolor: 'grey.100' }}>
              <TableRow>
                <TableCell>Tabell</TableCell>
                <TableCell>Attribut</TableCell>
                <TableCell>Typ</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.values(settings).map((setting: SettingRow) => (
                <React.Fragment key={setting.value}>
                  <TableRow
                    sx={{
                      bgcolor: 'grey.200',
                      position: 'sticky',
                      top: 37,
                      zIndex: 1
                    }}
                  >
                    <TableCell colSpan={3}>
                      <SectionHeader
                        size="small"
                        sx={{ pt: 0.5, '& .MuiStack-root': { mb: 0.25 } }}
                        title={setting.label}
                      />
                    </TableCell>
                  </TableRow>
                  {setting.attributes.map((attribute) => (
                    <TableRow hover={true} key={attribute.value}>
                      <TableCell> </TableCell>
                      <TableCell>
                        <Text variant="body1">{attribute.label}</Text>
                      </TableCell>
                      <TableCell>
                        {/*
                          The attribute's own checks, not every check there is:
                          DUPLICATE only runs on `id`, so listing it on a
                          business number would promise a check we don't make.
                        */}
                        {attribute.types.map((type) => (
                          <Tag
                            color={flawTypeColor[type]}
                            key={type}
                            label={
                              translateEnum('flaw_types', asFlawType(type))
                                .title
                            }
                          />
                        ))}
                      </TableCell>
                    </TableRow>
                  ))}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </SimpleDialog>
    </Box>
  )
}

DataHealthValidationTab.displayName = 'DataHealthValidationTab'
export default DataHealthValidationTab
